import { useEffect, useRef, useState } from 'react'
import { Button, IconButton, Tooltip, CircularProgress } from '@mui/material'
import { Grading } from '@mui/icons-material'
import { processDataService } from '../../services/processDataService'
import { useProcessContext, useFieldCurrencyMap } from '../../store'
import { getFieldId } from '../../pages/DataEntryPage/utils/utils'
import { ACTIVITY_TYPES, FIELD_TYPES, FORMATS } from '../../utility/constants'
import { fileServices } from '../../services/fileServices'
import { useAuthContext } from '../../hooks/useAuthContext'
import moment from 'moment'
import { extractDisplayName } from '../../utility/fileUtil'
import { formatCurrency } from '../../pages/DataEntryPage/utils/currency'
import LockingTooltip from '../LockingTooltip'

const FieldHistory = ({
  fieldId,
  field,
  children,
  processId = '',
  allValues,
  listDataMetadata = [],
  dynamicListData = {},
  isPreview = false,
  helperText = null,
  width = '100%',
}) => {
  const { user } = useAuthContext()
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const version = processContext?.processData?.version
  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)
  const defaultCurrency = processContext?.companyDetails?.default_currency_code

  const [reviewHistory, setReviewHistory] = useState({})

  const [tooltipStates, setTooltipStates] = useState({})

  const [showAllData, setShowAllData] = useState(false)

  const [tooltipPlacement, setTooltipPlacement] = useState('bottom')
  const fieldRef = useRef(null)

  useEffect(() => {
    const updateTooltipPlacement = () => {
      if (fieldRef.current) {
        const rect = fieldRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // If field is in the top half of the screen, show tooltip below; otherwise, show it above
        setTooltipPlacement(rect.top < viewportHeight / 2 ? 'bottom' : 'top')
      }
    }

    // Run the function once on mount and attach a resize listener
    updateTooltipPlacement()
    window.addEventListener('resize', updateTooltipPlacement)

    return () => {
      window.removeEventListener('resize', updateTooltipPlacement)
    }
  }, [])

  const handleToggleDataView = () => {
    setShowAllData((prev) => !prev) // Toggle state on click
  }

  const handleTooltipOpen = (fieldId) => {
    setTooltipStates((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        open: true,
      },
    }))
  }

  const handleTooltipClose = (fieldId) => {
    setTooltipStates((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        open: prev[fieldId]?.locked ? true : false,
      },
    }))
  }

  const handleViewHistoryClick = async (fieldId) => {
    try {
      // If data already exists, just toggle visibility
      if (reviewHistory[fieldId]?.data) {
        setReviewHistory((prev) => ({
          ...prev,
          [fieldId]: { ...prev[fieldId], showHistory: !prev[fieldId].showHistory },
        }))
        return
      }

      // Set loading state
      setReviewHistory((prev) => ({
        ...prev,
        [fieldId]: { data: [], showHistory: false, loading: true },
      }))

      const previousAllReviews = await processDataService.getAllPreviousReviews(
        user,
        fieldId,
        processId
      )

      const previousAllReviewsNotIncludedCurrent = previousAllReviews.data.filter(
        (review) => review.process_id !== processId
      )

      setReviewHistory((prev) => ({
        ...prev,
        [fieldId]: {
          data: previousAllReviewsNotIncludedCurrent,
          showHistory: true,
          loading: false,
        },
      }))
    } catch (error) {
      console.error('Error fetching review history:', error)
      setReviewHistory((prev) => ({
        ...prev,
        [fieldId]: { data: [], showHistory: false, loading: false },
      }))
    }
  }

  const value = allValues[getFieldId(fieldId)]
  const currencyCode = fieldCurrencyMap[field.id] ?? defaultCurrency ?? 'INR'

  let formattedValue

  if (field.data_structure === FIELD_TYPES.CURRENCY) {
    formattedValue = formatCurrency(value, currencyCode, {
      defaultCurrency: processContext.companyDetails?.default_currency_code,
    })
  } else {
    formattedValue = value || 'N/A'
  }

  const getReviewedData = (revDatum) => {
    if (!revDatum?.data) return null

    const ReviewStatus =
      revDatum?.review_data === true ? (
        <span style={{ fontSize: '9px', marginLeft: '9px' }}> &#9989;</span>
      ) : revDatum?.review_data === false ? (
        <span style={{ fontSize: '9px', marginLeft: '9px' }}> &#10060;</span>
      ) : null

    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
      case FIELD_TYPES.LIST_MULTI:
        const listItem = getListActualValue(
          field,
          fieldId,
          allValues,
          listDataMetadata,
          dynamicListData
        )

        return listItem ? (
          <>
            <strong>{listItem}</strong> {ReviewStatus}
          </>
        ) : null

      case FIELD_TYPES.DATE:
        return (
          <>
            <strong>{moment(revDatum.data).format(FORMATS.DATE)}</strong> {ReviewStatus}
          </>
        )

      case FIELD_TYPES.DATETIME:
        return (
          <>
            <strong>{moment(revDatum.data).format(FORMATS.DATETIME)}</strong> {ReviewStatus}
          </>
        )

      case FIELD_TYPES.CURRENCY: {
        const currencyCode = fieldCurrencyMap[field.id] ?? defaultCurrency ?? 'INR'
        const revFormattedValue = formatCurrency(revDatum.data, currencyCode, {
          defaultCurrency: processContext.companyDetails?.default_currency_code,
        })

        return (
          <>
            <strong>{revFormattedValue}</strong> {ReviewStatus}
          </>
        )
      }
      case FIELD_TYPES.FILE:
        return (
          <span style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>
              {' '}
              <strong>{extractDisplayName(revDatum.data)}</strong> {ReviewStatus}
            </span>
            <Button
              onClick={() => fileServices.downloadFile(user, revDatum.data)}
              variant="outlined"
              style={{
                height: '19px',
                width: '20px',
                color: 'white',
                borderColor: 'white',
                marginLeft: '12px',
              }}
            >
              &#11015;
            </Button>
          </span>
        )
      case FIELD_TYPES.TIMESTAMPED_BIGTEXT: {
        const timestampedValues = revDatum.data || []
        let formattedText = timestampedValues.map((item, index) => {
          const user = dynamicListData?.users?.find((u) => u.id === item?.user_id)
          const username = user ? `${user.first_name} ${user.last_name}` : null

          const lines = item.text.split('\n').map((line, i) => (
            <span key={`${index}-${i}`}>
              {line}
              <br />
            </span>
          ))

          return (
            <div key={index}>
              <i>{moment(item.timestamp).format(FORMATS.DATETIME)}:</i> {lines}{' '}
              {username ? ` - ${username}` : ''}
            </div>
          )
        })

        return (
          <div>
            {formattedText} {ReviewStatus}
          </div>
        )
      }

      default:
        return (
          <>
            <strong>{revDatum.data}</strong> {ReviewStatus}
          </>
        )
    }
  }

  const getListActualValue = (field, fieldId, allValues, listDataMetadata, dynamicListData) => {
    if (!field || !fieldId || !allValues) return ''
    const fieldKey = getFieldId(fieldId)
    const fieldValue = allValues[fieldKey]

    if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) return ''

    if (field.list_id == null) {
      const type = field?.dynamic_field_type?.type
      const listData =
        type === 'users'
          ? isPreview
            ? dynamicListData?.users
            : dynamicListData?.usersList
          : isPreview
          ? dynamicListData?.teams
          : dynamicListData?.teamsList

      if (Array.isArray(fieldValue)) {
        const values = fieldValue
          .map((id) => {
            const item = listData?.find((it) => it.id == id)
            if (!item) return null
            if (type === 'users') {
              return `${item.first_name || ''} ${item.last_name || ''}`.trim()
            } else if (type === 'teams') {
              return item.name || item.value || ''
            }
            return item.value || ''
          })
          .filter(Boolean)
        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '6px', lineHeight: '1.4em' }}
          >
            {values.map((val, index) => (
              <div key={index}>{val}</div>
            ))}
          </div>
        )
      } else {
        const item = listData?.find((it) => it.id == fieldValue)
        if (!item) return ''
        if (type === 'users') {
          return `${item.first_name || ''} ${item.last_name || ''}`.trim()
        } else if (type === 'teams') {
          return item.name || item.value || ''
        }
        return item.value || ''
      }
    }

    // Handle static list (LIST or LIST_MULTI)
    if (Array.isArray(fieldValue)) {
      // LIST_MULTI
      const values = fieldValue
        .map((id) => {
          const entry = isPreview
            ? listDataMetadata?.find((it) => it.id == id)
            : listDataMetadata?.find((it) => it.list_data_id == id)
          return entry?.list_data_value || entry?.value || null
        })
        .filter(Boolean)
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            lineHeight: '1.6em',
            lineBreak: 'anywhere',
            gap: '8px', // space between lines
          }}
        >
          {values.map((val, index) => (
            <div key={index}>{val}</div>
          ))}
        </div>
      )
    } else {
      // LIST
      const entry = isPreview
        ? listDataMetadata?.find((it) => it.id == fieldValue)
        : listDataMetadata?.find((it) => it.list_data_id == fieldValue)
      return entry?.list_data_value || entry?.value || ''
    }
  }

  return (
    <LockingTooltip
      style={{ width: width ? width : '100%' }}
      slotProps={{
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              minWidth: reviewHistory[fieldId]?.showHistory ? '375px !important' : 'auto',
              maxWidth: '475px',
              whiteSpace: 'normal',
              border: 'none !important',
            },
          },
        },
      }}
      placement={tooltipPlacement}
      open={tooltipStates[fieldId]?.open || false}
      onOpen={() => handleTooltipOpen(fieldId)}
      onClose={() => handleTooltipClose(fieldId)}
      title={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '5px',
            overflowY: 'auto',
            padding: '10px !important',
            maxHeight: '370px',
          }}
        >
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '12px' }}>
                <span
                  style={{
                    fontWeight: reviewHistory[fieldId]?.showHistory && 'bolder',
                  }}
                >
                  {field.field_template_name}:{' '}
                </span>

                {helperText ? (
                  helperText
                ) : (
                  <span>
                    {field.data_structure === FIELD_TYPES.LIST ||
                    field.data_structure === FIELD_TYPES.LIST_MULTI ? (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {getListActualValue(
                          field,
                          fieldId,
                          allValues,
                          listDataMetadata,
                          dynamicListData
                        ) || 'Please select an option'}
                      </span>
                    ) : field.data_structure === FIELD_TYPES.DATE &&
                      allValues[getFieldId(fieldId)] ? (
                      <>{moment(allValues[getFieldId(fieldId)]).format(FORMATS.DATE)}</>
                    ) : field.data_structure === FIELD_TYPES.DATETIME &&
                      allValues[getFieldId(fieldId)] ? (
                      <>{moment(allValues[getFieldId(fieldId)]).format(FORMATS.DATETIME)}</>
                    ) : field.data_structure === FIELD_TYPES.FILE &&
                      allValues[getFieldId(fieldId)] ? (
                      <>{extractDisplayName(allValues[getFieldId(fieldId)])}</>
                    ) : field.data_structure === FIELD_TYPES.CURRENCY &&
                      allValues[getFieldId(fieldId)] ? (
                      <> {formattedValue}</>
                    ) : field.data_structure === FIELD_TYPES.TIMESTAMPED_BIGTEXT ? (
                      <>
                        {!value || !value.length ? (
                          <div>No entries yet.</div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {value.map((v, index) => {
                              const user = dynamicListData?.users?.find((u) => u.id === v.user_id)
                              const username = user ? `${user.first_name} ${user.last_name}` : null

                              return (
                                <div
                                  key={index}
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    padding: '8px 12px',
                                    gap: '6px',
                                  }}
                                >
                                  <div
                                    style={{ flex: '0 0 120px', fontSize: '11px', fontWeight: 600 }}
                                  >
                                    {v.timestamp}
                                  </div>

                                  <div
                                    style={{ flex: 1, fontSize: '12px', whiteSpace: 'pre-wrap' }}
                                  >
                                    {v.text}
                                    {username && (
                                      <span
                                        style={{
                                          fontSize: '11px',
                                          fontStyle: 'italic',
                                          color: '#888',
                                          marginLeft: 6,
                                        }}
                                      >
                                        - {username}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {allValues[getFieldId(fieldId)]}
                      </span>
                    )}
                  </span>
                )}
              </span>
              <span style={{ marginTop: '-5px' }}>
                {reviewHistory[fieldId]?.showHistory &&
                  !(
                    (activityType === ACTIVITY_TYPES.DATA_ENTRY && version == 1) ||
                    field.is_computed ||
                    field.is_read_only
                  ) && (
                    <Tooltip title={showAllData ? 'hide reviews' : 'show reviews'}>
                      <IconButton onClick={handleToggleDataView}>
                        <Grading style={{ fontSize: '16px' }} />
                        {showAllData && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '46%',
                              left: '6px',
                              width: '60%',
                              height: '2px',
                              backgroundColor: 'red',
                              transform: 'rotate(-45deg)',
                            }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
              </span>
            </div>
            {reviewHistory[fieldId]?.showHistory && <hr style={{ margin: '0px 0px 10px 0px' }} />}
          </div>

          {reviewHistory[fieldId]?.loading ? (
            <CircularProgress size={20} color="primary" />
          ) : (
            reviewHistory[fieldId]?.showHistory &&
            reviewHistory[fieldId]?.data?.length > 0 &&
            reviewHistory[fieldId].data
              .filter(
                (revDatum) => showAllData || revDatum.activity_type === ACTIVITY_TYPES.DATA_ENTRY
              )
              .map((revDatum, index, filteredArray) => (
                <div key={index}>
                  <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{getReviewedData(revDatum)}</p>

                  <p style={{ margin: 0 }}>
                    {revDatum?.user_name?.length > 0 ? (
                      <>{revDatum?.user_name}</>
                    ) : (
                      <>Not reviewed yet!!</>
                    )}{' '}
                  </p>

                  {revDatum?.activity_type == ACTIVITY_TYPES.REVIEW ? (
                    <p style={{ margin: 0 }}>
                      Review of "{revDatum?.associated_data_entry_activity_template_name}" version{' '}
                      {revDatum?.version}
                    </p>
                  ) : (
                    <p style={{ margin: 0 }}>
                      Activity: "{revDatum?.activity_template_name}" version {revDatum?.version}
                    </p>
                  )}

                  {revDatum?.activity_type === ACTIVITY_TYPES.REVIEW && (
                    <p style={{ margin: 0, whiteSpace: 'pre-line' }}>
                      Review Comment: {revDatum?.review_comments_data}
                    </p>
                  )}

                  {revDatum?.activity_type == ACTIVITY_TYPES.REVIEW ? (
                    <p style={{ justifyContent: 'end', margin: 0 }}>
                      Review date: {moment(revDatum?.modified_at).format(FORMATS.DATETIME)}
                    </p>
                  ) : (
                    <p style={{ justifyContent: 'end', margin: 0 }}>
                      Date: {moment(revDatum?.modified_at).format(FORMATS.DATETIME)}
                    </p>
                  )}
                  {index !== filteredArray.length - 1 && <hr />}
                </div>
              ))
          )}
          {!reviewHistory[fieldId]?.showHistory &&
            !reviewHistory[fieldId]?.loading &&
            !(
              (activityType === ACTIVITY_TYPES.DATA_ENTRY && version == 1) ||
              field.is_computed ||
              field.is_read_only
            ) && (
              <span
                onClick={() => handleViewHistoryClick(fieldId)}
                size="small"
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => (e.target.style.color = 'gray')}
                onMouseLeave={(e) => (e.target.style.color = null)}
              >
                View history
              </span>
            )}
        </div>
      }
    >
      <span className={`cell-value-inside-field-history ${isPreview ? 'table-context' : ''}`}>
        {children}
      </span>
    </LockingTooltip>
  )
}

export default FieldHistory
